import { z } from "zod";
import { TASK_STATUSES } from "../domain";

// Bucket 11 — admin journeys builder. Replaces /app/admin/journeys/_actions.ts.
//
// The legacy domain Zod schemas (journeyCreateSchema, journeyUpdateSchema,
// journeyTaskCreateSchema, journeyTaskUpdateSchema, journeyTaskActionSchema,
// assignJourneyInputSchema) already live in `../domain` and are re-exported
// from `@colab/shared` via the package index. This file only adds the
// request-body schemas for the two endpoints whose wire shape differs from
// the domain CRUD schemas — the flat task reorder and the bulk assignment.

// PATCH /api/admin/journeys/:id/tasks/order — the whole ordered task id list,
// one PATCH per drop. Same DnD convention as bucket 5 forms-field reorder;
// flat (vs. forms' nested sections) because journey tasks have no grouping
// hierarchy — `journey_tasks.position` is the only ordering column.
export const journeyTaskReorderRequestSchema = z.object({
  taskIds: z.array(z.string().uuid()).min(1),
});
export type JourneyTaskReorderRequest = z.infer<
  typeof journeyTaskReorderRequestSchema
>;

// POST /api/admin/journeys/:id/assignments — bulk assignment to N partners.
// Mirrors the legacy `assignJourneyToPartnersAction(journeyId, partnerIds)`
// signature; the journey id lives in the URL, the partner ids in the body.
export const journeyAssignRequestSchema = z.object({
  partnerIds: z.array(z.string().uuid()).min(1),
});
export type JourneyAssignRequest = z.infer<typeof journeyAssignRequestSchema>;

// Bucket 12 — partner-side task-progress mutations. Replaces
// /app/portal/journeys/_actions.ts.

// PATCH /api/portal/journeys/progress/:progressId — flip status. The shape
// collapses the legacy markProgress(Complete|InProgress|Pending)Action trio
// into one endpoint with `{ status }` in the body, mirroring how bucket 11
// collapsed the journeyTaskAction discriminated-union update into a single
// PATCH.
//
// Sub-bucket 4 extends the accepted set with 'snoozed', but the canonical
// snooze entry point is the dedicated /snooze endpoint (which also captures
// snoozed_until + snooze_reason). The bucket-12 PATCH is left as a thin
// status flip so existing optimistic hooks don't churn.
export const portalJourneyProgressStatusRequestSchema = z.object({
  status: z.enum(TASK_STATUSES),
});
export type PortalJourneyProgressStatusRequest = z.infer<
  typeof portalJourneyProgressStatusRequestSchema
>;

// POST /api/portal/journeys/progress/:progressId/form-submission — JSON
// shape that replaces the legacy FormData + JSON.parse-per-value loop. The
// SPA's FormRenderer already produces structured payloads; the worker re-
// validates against the form's definition via validateSubmissionPayload
// (bucket 6 reuse).
export const portalJourneyFormSubmitRequestSchema = z.object({
  formId: z.string().uuid(),
  payload: z.record(z.string(), z.unknown()),
});
export type PortalJourneyFormSubmitRequest = z.infer<
  typeof portalJourneyFormSubmitRequestSchema
>;

// =============================================================================
// Sub-bucket 4 — partner-side journey extensions: snooze, proof payload,
// per-task comments + attachments, explicit journey-complete gesture.
// =============================================================================

// POST /api/portal/journeys/progress/:progressId/snooze
//
// snoozed_until is required; snoozeReason is optional human-authored copy
// (e.g. "Awaiting brief from client"). The route enforces snoozed_until is
// in the future at call time.
export const portalJourneySnoozeRequestSchema = z.object({
  snoozed_until: z.string().datetime({ offset: true }),
  snooze_reason: z.string().trim().min(1).max(500).nullable().optional(),
});
export type PortalJourneySnoozeRequest = z.infer<
  typeof portalJourneySnoozeRequestSchema
>;

// POST /api/portal/journeys/progress/:progressId/proof
//
// Records the proof payload alongside (optionally) flipping status to
// completed. The route validates payload.kind matches the linked task's
// action.kind. completeOnRecord defaults true — the dominant flow is
// "record + complete in one click".
export const portalJourneyProofRequestSchema = z.object({
  // Re-validated against the linked task's action.kind in the service layer.
  // Keeping the discriminator open here so the schema doesn't need to know
  // the task's kind at parse time.
  payload: z.discriminatedUnion("kind", [
    z
      .object({
        kind: z.literal("note"),
        note: z.string().trim().min(1).max(4000),
      })
      .strict(),
    z
      .object({ kind: z.literal("link"), url: z.string().trim().url().max(2048) })
      .strict(),
    z
      .object({
        kind: z.literal("attachment"),
        attachment_ids: z.array(z.string().uuid()).min(1).max(10),
      })
      .strict(),
  ]),
  complete_on_record: z.boolean().default(true),
});
export type PortalJourneyProofRequest = z.infer<
  typeof portalJourneyProofRequestSchema
>;

// POST /api/portal/journeys/progress/:progressId/comments — partner adds a
// comment to a task thread. visibility is forced to 'partner' on the route
// (mirroring mdfCommentCreateSchema). Body length matches DB CHECK
// (1..4000 chars after btrim).
export const journeyTaskCommentCreateSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type JourneyTaskCommentCreateInput = z.infer<
  typeof journeyTaskCommentCreateSchema
>;

// Attachments — three-step upload, identical envelope to MDF + forms.
export const JOURNEY_ATTACHMENT_MAX_BYTES = 26_214_400; // 25 MB

export const journeyAttachmentUploadInitSchema = z.object({
  progress_id: z.string().uuid(),
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(JOURNEY_ATTACHMENT_MAX_BYTES),
});
export type JourneyAttachmentUploadInitInput = z.infer<
  typeof journeyAttachmentUploadInitSchema
>;

export const journeyAttachmentFinalizeSchema = z.object({
  progress_id: z.string().uuid(),
  storage_key: z.string().min(1).max(512),
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(JOURNEY_ATTACHMENT_MAX_BYTES),
});
export type JourneyAttachmentFinalizeInput = z.infer<
  typeof journeyAttachmentFinalizeSchema
>;

// MIME allowlist mirrors MDF + form attachment policy. Enforced in the
// service layer on the upload-init handler.
export const JOURNEY_ATTACHMENT_MIME_ALLOWLIST = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;
export type JourneyAttachmentMime =
  (typeof JOURNEY_ATTACHMENT_MIME_ALLOWLIST)[number];
